/**
 * @type {{Client: Paho.Client, Message: Paho.Message}}
 */
import { Client } from '@megogo-interactive/paho-client';
import scopeLogger from 'lib/scope-logger';
import { tokenContent } from 'lib/token-content.js';
import { Kibana } from '#/utils/trackKibana.js';

const console = scopeLogger('RMI:MNS');

const CONNECTION_CHANGED = 'connection changed';
const listeners = new Map();
const subscriptions = new Map();

const kibana = new Kibana('MNS');

/**
 *
 * @param config
 * @param down$
 * @return {MNSInterface}
 * @constructor
 */
export default function MNS(config /*, up$*/, down$) {
  const {
    client_id,
    broker_url,
    useSSL,
    port,
    broker_ping_interval: keepAliveInterval,
    userName,
    token: password,
  } = config;
  const connectionOptions = {
    userName,
    password,
    reconnect: false,
    keepAliveInterval,
    useSSL,
    mqttVersion: 4,
    mqttVersionExplicit: true,
  };
  const notify = (type, payload) => {
    down$.next({
      topic: 'internal',
      payload: {
        type,
        ...payload,
      },
    });
  };
  let connectTimeout;
  let pahoClient;
  let connectionLostTimestamp;

  try {
    pahoClient = new Client(broker_url, port, client_id);

    pahoClient.onConnected = onConnected;
    pahoClient.onMessageArrived = onMessage;
    // necessary for very deep debug only
    if (import.meta.env.VITE_APP_MNS_DEBUG) {
      pahoClient.trace = ({ message }) => {
        console.log(message);
      };
    }

    connect();
  } catch (error) {
    kibana.error(error);
    notify('error', {
      code: 999, // ????
      message: error.message,
    });
  }


  /* TODO: extract to own module, pass suspend/wakeup to MNS via command or API */
  let visibilityTimer;
  document.addEventListener('visibilitychange', () => {
    if (document.visibilityState === 'hidden') {
      clearTimeout(visibilityTimer);
      visibilityTimer = setTimeout(() => {
        if (document.visibilityState === 'visible') {
          return;
        }
        if (pahoClient?.isConnected()) {
          pahoClient.onConnectionLost = () => {
            console.log('disconnect on hidden');
            kibana.info('disconnect on hidden');
          };
          pahoClient.disconnect();
        }
        connectTimeout = clearTimeout(connectTimeout);
      }, 30_000);
    } else {
      clearTimeout(visibilityTimer);
      visibilityTimer = setTimeout(() => {
        if (document.visibilityState === 'hidden') {
          return;
        }
        console.log('connect on visible');
        kibana.info('connect on visible');
        reconnect();
      }, 10_000);
    }
  });

  return {
    updateConfig(newConfig) {
      if (!pahoClient) {
        // something very wrong
        kibana.error('updateConfig: no client');

        // panic
        notify('error', {
          code: 999, // ????
          message: 'no MNS client',
        });
        return;
      }

      connectionOptions.password = newConfig.token;

      if (pahoClient.isConnected()) {
        // establish new connect only after successful disconnect
        pahoClient.onConnectionLost = () => {
          connect();
        };
        pahoClient.disconnect();
      } else {
        connect();
      }
    },

    /**
     * setup external listener for connection change "event"
     */
    set onConnectionChange(cb) {
      listeners.set(CONNECTION_CHANGED, cb);
    },
    /*
        bindStore(_store){
          // subscribe on MNS slice to re-init, re-connect
        },
    */
    publish(topic, message) {
      const payload = JSON.stringify({
        ...message,
        client_id: pahoClient.clientId,
      });
      if (import.meta.env.DEV) {
        console.debug(
          `%c⤊ to ${topic}`,
          'color: green; font-weight: bold;',
          message,
        );
      }

      try {
        pahoClient.send(topic, payload);
      } catch (error) {
        if (error === 'AMQJS0011E Invalid state not connected.') {
          reconnect();
        }
        throw error;
      }
    },
    subscribe(topic, ctx = {}) {
      const log = [`subscribe to ${topic}`];
      if (subscriptions.has(topic)) {
        subscriptions.get(topic).counter++;
        log.push(
          `-- already subscribed, new counter ${
            subscriptions.get(topic).counter
          }`,
        );
        console.log(log.join('\n'));
        return Promise.resolve(ctx);
      }

      subscriptions.set(topic, { counter: 1 });

      log.push('-- new subscription, counter: 1');

      if (!pahoClient.isConnected()) {
        log.push('not connected, just add to subscription list');
        console.log(log.join('\n'));
        reconnect();
        return Promise.resolve();
      }

      return new Promise((resolve, reject) => {
        pahoClient.subscribe(topic, {
          invocationContext: ctx,
          onSuccess: ({ invocationContext }) => {
            log.push('-- subscription success');
            console.log(log.join('\n'));
            kibana.info('subscribe', {
              topic,
            });
            resolve(invocationContext);
          },
          onFailure: ({ invocationContext, errorCode, errorMessage }) => {
            const error = new Error(errorMessage);
            error.code = errorCode;
            error.ctx = invocationContext;

            reject(error);
          },
        });
      }).catch((err) => {
        log.push('-- subscription failure');
        console.log(log.join('\n'));
        const error = new Error(err.message);
        error.code = error.code;
        error.topic = topic;
        kibana.error(error);
        throw error;
      });
    },
    unsubscribe(topic, ctx = {}) {
      const log = [`unsubscribe from ${topic}`];
      const sub = subscriptions.get(topic);
      if (!sub || --sub.counter > 0) {
        log.push(
          `-- avoid unsubscribe on used topic, new counter ${sub.counter}`,
        );
        console.log(log.join('\n'));
        return Promise.resolve(ctx);
      }

      subscriptions.delete(topic);
      log.push('-- remove subscription');
      if (!pahoClient.isConnected()) {
        log.push('not connected, just remove subscription from list');
        console.log(log.join('\n'));
        return Promise.resolve();
      }

      return new Promise((resolve, reject) => {
        pahoClient.unsubscribe(topic, {
          invocationContext: ctx,
          onSuccess: ({ invocationContext }) => {
            log.push('-- unsubscription success');
            console.log(log.join('\n'));
            resolve(invocationContext);
          },
          onFailure: ({ invocationContext }) => {
            log.push('-- unsubscription success');
            console.log(log.join('\n'));
            reject(invocationContext);
          },
        });
      });
    },
  };

  function onConnected() {
    console.log('connected');
    connectTimeout = clearTimeout(connectTimeout);

    pahoClient.onConnectionLost = onConnectionLost;

    subscriptions.forEach((sub, topic) => {
      console.log('re-subscribe on connect', topic);
      pahoClient.subscribe(topic);
    });

    listeners.get(CONNECTION_CHANGED)?.(true);

    kibana.info('connected');
  }

  function onConnectionLost(error) {
    console.log('onConnectionLost', error);
    listeners.get(CONNECTION_CHANGED)?.(false);
    if (error.errorCode === 0) {
      // normal disconnect
      return;
    }
    connectionLostTimestamp = Date.now();
    connect();
  }

  function onMessage(message) {
    try {
      message.payload = JSON.parse(message.payloadString);
    } catch (error) {
      message.payload = message.payloadString;
    }
    down$.next(message);
  }

  function reconnect() {
    if (connectTimeout) {
      // all ready have some connecting
      return;
    }
    connect();
  }

  function connect(tryCounter = 0) {
    if (pahoClient?.isConnected()) {
      return;
    }

    try {
      console.log(
        'try to connect using token',
        tokenContent(connectionOptions.password),
      );

      connectTimeout = setTimeout(() => {
        /*
         * clone object, Paho attempt to extend options,
         * it is not available for sealed objects
         */
        pahoClient.connect({
          ...connectionOptions,
          invocationContext: {attempt: tryCounter},
          onFailure: onConnectFailure,
          onSuccess({ invocationContext }) {
            const isReconnect = Number.isFinite(connectionLostTimestamp);
            if (isReconnect) {
              const offlineDuration = Math.floor(Date.now() - connectionLostTimestamp);
              connectionLostTimestamp = undefined;

              if (offlineDuration > 2_000) {
                kibana.info('reconnected', {
                  retry: invocationContext.attempt,
                  offlineDuration,
                });
              }
            }
          }
        });
      });
    } catch (error) {
      if (error.message === 'AMQJS0011E Invalid state already connected.') {
        return;
      }
      // the reason for all other exceptions is invalid arguments
      console.error('connect error', error);
      kibana.error(error);
      // TODO: handle such cases
    }

  }

  function onConnectFailure(response) {
    if (
      response.errorCode === 6 &&
      response.errorMessage ===
      'AMQJS0006E Bad Connack return code:5 Connection Refused: not authorized.'
    ) {
      console.warn('handled failure: Unauthorized');
      kibana.warn('Unauthorized');

      notify('error', {
        code: 401,
        message: 'Unauthorized',
      });

      return; /* expecting to re-connect via updateConfig with new token */
    }

    console.error('connect failure', response);

    connectTimeout = clearTimeout(connectTimeout);
    const { attempt: tryCounter } = response.invocationContext;
    if (tryCounter < 10) {
      const timeout = Math.min(120_000, 2_000 * 2 ** tryCounter);
      console.log(
        `Re try connect #${tryCounter + 1} in ${timeout/1e3}s.`,
      );
      connectTimeout = setTimeout(connect, timeout, tryCounter + 1);

    } else {
      console.error('too many attempts to connect');
      // TODO: handle such cases:
      //  - self reload?
      //  - send panic message to host?
    }
  }

}

/**
 * @typedef {Object} MNSInterface
 * @property {function} updateConfig
 * @property {function} publish
 * @property {function} subscribe
 * @property {function} unsubscribe
 * @property {function} onConnectionChange
 */
